import { createContext, ReactNode, useContext, useState } from "react"
import { faRightLeft, faCompressArrowsAlt, faTrash } from "@fortawesome/free-solid-svg-icons"
import Button from "@/components/button"
import ModalConfirm from "@/components/modal-confirm"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { STATUS_SAVING } from "@/redux/_status.types"
import physicalRiskStore from "../model/physical-risk.store"
import preventionActionStore from "@/entities/prevention-action/model/prevention-action.store"
import { toast } from "react-toastify"
import PhysicalRiskMergeModal from "./modal/PhysicalRiskMergeModal"
import PhysicalRiskMoveToRpsModal from "./modal/PhysicalRiskMoveToRpsModal"

const SelectionContext = createContext<{
  selectedIds: string[]
  toggle: (id: string) => void
  remove: (id: string) => void
} | null>(null)

export const usePhysicalRiskSelection = () => useContext(SelectionContext)

export const isInteractiveTarget = (target: Element) =>
  Boolean(target.closest("button, a, input, select, textarea, [role='button'], .link"))

// Le bandeau et ses cartes partagent une selection locale, remise a zero par la cle de page.
export function PhysicalRiskSelectionProvider({ children }: { children: ReactNode }) {
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  return (
    <SelectionContext.Provider value={{
      selectedIds,
      toggle: (id) => setSelectedIds((ids) => ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id]),
      remove: (id) => setSelectedIds((ids) => ids.filter((item) => item !== id))
    }}>
      {children}
    </SelectionContext.Provider>
  )
}

export function PhysicalRiskSelectionActions() {
  const selection = usePhysicalRiskSelection()
  const dispatch = useAppDispatch()
  const risks = useAppSelector(physicalRiskStore.selectors.list)
  const preventionActions = useAppSelector(preventionActionStore.selectors.list)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isMergeOpen, setIsMergeOpen] = useState(false)
  const [isMoveOpen, setIsMoveOpen] = useState(false)
  const selectedIds = selection?.selectedIds.filter((id) => risks.some((risk) => risk.id === id)) ?? []

  async function deleteSelectedRisks() {
    if (isDeleting) return
    setIsDeleting(true)
    try {
      for (const id of selectedIds) {
        const response = await dispatch(physicalRiskStore.thunks.removeEntity(id)).unwrap()
        if (!response) throw new Error()
        dispatch(preventionActionStore.actions.removeMany(
          preventionActions.filter((action) => action.PhysicalRiskId === id).map((action) => action.id)
        ))
        selection?.remove(id)
      }
      setIsConfirmOpen(false)
    } catch {
      toast.error("La suppression a échoué. Les risques restants sont toujours sélectionnés.")
    } finally {
      setIsDeleting(false)
    }
  }

  //La modale de deplacement reste montee apres coup : les risques deplaces quittent la selection,
  //mais son recap doit rester affiche
  if (!selectedIds.length && !isMoveOpen) return null

  return (
    <>
      {selectedIds.length > 0 && (
        <>
          <Button
            icon={faTrash}
            tooltip={`Supprimer les risques sélectionnés (${selectedIds.length})`}
            disabled={isDeleting}
            onClick={() => setIsConfirmOpen(true)}
          />
          <Button
            icon={faRightLeft}
            tooltip="Déplacer les risques sélectionnés vers RPS"
            disabled={isDeleting}
            onClick={() => setIsMoveOpen(true)}
          />
        </>
      )}
      {selectedIds.length >= 2 && (
        <Button
          icon={faCompressArrowsAlt}
          tooltip="Fusionner les risques sélectionnés"
          disabled={isDeleting}
          onClick={() => setIsMergeOpen(true)}
        />
      )}
      {isMoveOpen && (
        <PhysicalRiskMoveToRpsModal
          riskIds={selectedIds}
          onClose={() => setIsMoveOpen(false)}
          onMoved={(ids) => ids.forEach((id) => selection?.remove(id))}
        />
      )}
      {isMergeOpen && (
        <PhysicalRiskMergeModal
          riskIds={selectedIds}
          onClose={() => setIsMergeOpen(false)}
          onMerged={() => selectedIds.forEach((id) => selection?.remove(id))}
        />
      )}
      {isConfirmOpen && (
        <ModalConfirm
          status={isDeleting ? STATUS_SAVING : undefined}
          onNo={() => { if (!isDeleting) setIsConfirmOpen(false) }}
          onYes={deleteSelectedRisks}
          text={`Vous allez supprimer ${selectedIds.length} risque(s) dans tout l'établissement, ainsi que leurs mesures de prévention associées.`}
          textBold="Souhaitez-vous continuer ?"
        />
      )}
    </>
  )
}
